import type { Ctx } from "../app";
import { must, sb } from "../lib/supabase";
import { Badge, Card, ErrorBox, Loading, daysUntil, fmtDate, useLoad } from "../lib/ui";

export function Dashboard({ ctx }: { ctx: Ctx }) {
  const { data, error } = useLoad(async () => {
    const [billable, stuck, exp, apps, tasks, provs] = await Promise.all([
      sb().from("v_billable_now").select("*").eq("org_id", ctx.orgId).order("provider_name"),
      sb().from("v_stuck").select("*").eq("org_id", ctx.orgId).order("days_since_submitted", { ascending: false }),
      sb().from("v_expirations").select("*").eq("org_id", ctx.orgId)
        .lte("due_on", new Date(Date.now() + 60 * 86400000).toISOString().slice(0, 10)).order("due_on"),
      sb().from("applications").select("status").eq("org_id", ctx.orgId),
      sb().from("tasks").select("id", { count: "exact", head: true }).eq("org_id", ctx.orgId).is("done_at", null)
        .lte("due_on", new Date().toISOString().slice(0, 10)),
      sb().from("providers").select("id, first_name, last_name").eq("org_id", ctx.orgId),
    ]);
    const names = new Map((must(provs) as Array<{ id: string; first_name: string; last_name: string }>).map((p) => [p.id, `${p.first_name} ${p.last_name}`]));
    const counts: Record<string, number> = {};
    for (const a of must(apps) as Array<{ status: string }>) counts[a.status] = (counts[a.status] ?? 0) + 1;
    return { billable: must(billable) as any[], stuck: must(stuck) as any[], exp: (must(exp) as any[]).map((e) => ({ ...e, provider_name: e.provider_id ? names.get(e.provider_id) : null })), counts, dueTasks: tasks.count ?? 0 };
  }, [ctx.orgId]);

  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const inFlight = ["submitted", "in_review", "info_requested"].reduce((n, s) => n + (data.counts[s] ?? 0), 0);

  return (
    <>
      <div class="stats">
        <Stat label="Billable provider × payer pairs" value={data.billable.filter((b) => b.credentials_current).length} />
        <Stat label="Applications in flight" value={inFlight} />
        <Stat label="Stuck / overdue" value={data.stuck.length} tone={data.stuck.length ? "bad" : undefined} />
        <Stat label="Tasks due today" value={data.dueTasks} href="#/tasks" />
      </div>

      <Card title="Who is billable right now">
        {data.billable.length === 0 ? <p class="muted">No effective approvals recorded yet.</p> : (
          <table>
            <thead><tr><th>BCBA</th><th>Payer</th><th>Effective</th><th>Credentials</th></tr></thead>
            <tbody>{data.billable.map((b) => (
              <tr>
                <td><a href={`#/providers/${b.provider_id}`}>{b.provider_name}</a></td>
                <td>{b.payer_name}</td><td>{fmtDate(b.effective_on)}</td>
                <td>{b.credentials_current ? <Badge value="met" /> : <span class="badge bad">expired credential — not billable</span>}</td>
              </tr>))}</tbody>
          </table>
        )}
      </Card>

      <Card title="What is stuck">
        {data.stuck.length === 0 ? <p class="muted">Nothing stuck. 🎉</p> : (
          <table>
            <thead><tr><th>BCBA</th><th>Payer</th><th>Status</th><th>Why</th><th>Submitted</th><th>Next follow-up</th></tr></thead>
            <tbody>{data.stuck.map((s) => (
              <tr>
                <td>{s.provider_name}</td>
                <td><a href={`#/applications/${s.application_id}`}>{s.payer_name}</a></td>
                <td><Badge value={s.status} /></td>
                <td>{s.reason === "blocked" ? s.blocked_reason : s.reason.replaceAll("_", " ")}</td>
                <td>{fmtDate(s.submitted_on)}{s.days_since_submitted != null && <span class="muted"> ({s.days_since_submitted}d)</span>}</td>
                <td>{fmtDate(s.next_follow_up_on)}</td>
              </tr>))}</tbody>
          </table>
        )}
      </Card>

      <Card title="Expiring in the next 60 days">
        {data.exp.length === 0 ? <p class="muted">Nothing expiring soon.</p> : (
          <table>
            <thead><tr><th>Due</th><th>BCBA</th><th>Item</th></tr></thead>
            <tbody>{data.exp.map((e) => {
              const d = daysUntil(e.due_on);
              return (
                <tr>
                  <td><span class={`badge ${d < 0 ? "bad" : d <= 30 ? "warn" : ""}`}>{d < 0 ? `${-d}d overdue` : `${d}d`}</span> {fmtDate(e.due_on)}</td>
                  <td>{e.provider_name ? <a href={`#/providers/${e.provider_id}`}>{e.provider_name}</a> : "Group contract"}</td>
                  <td>{e.item.replaceAll("_", " ")}</td>
                </tr>);
            })}</tbody>
          </table>
        )}
      </Card>
    </>
  );
}

function Stat({ label, value, tone, href }: { label: string; value: number; tone?: string; href?: string }) {
  const inner = <><div class={`stat-value ${tone ?? ""}`}>{value}</div><div class="muted small">{label}</div></>;
  return href ? <a class="stat" href={href}>{inner}</a> : <div class="stat">{inner}</div>;
}
